// src/pages/PartnerPerformancePage.js
import React, { useState, useEffect } from 'react';
import { 
  Target, Users, Save, RefreshCw, Search, TrendingUp, AlertCircle, Download 
} from 'lucide-react';
import { 
  collection, doc, getDoc, setDoc, onSnapshot, addDoc, serverTimestamp, query, orderBy, limit 
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import * as XLSX from 'xlsx'; // 👈 TAMBAHAN: Import library Excel

export default function PartnerPerformancePage() {
  // 1. State untuk Target Berjenjang & Region Aktif
  const [targets, setTargets] = useState({}); // Menyimpan struktur target semua kota
  const [activeRegion, setActiveRegion] = useState('jabodetabek'); // Area yang sedang dibuka admin
  const [activeRole, setActiveRole] = useState('therapist'); // 👈 TAMBAHAN: Filter Divisi Aktif
  
  // Kunci kombinasi untuk membaca/menyimpan ke Firestore (Contoh: 'jabodetabek_therapist')
  const activeKey = `${activeRegion}_${activeRole}`;

  // Helper: Mendeteksi area mitra (Prioritas: Manual Override -> Otomatis Cities -> Default)
  const getPartnerRegion = (mitra) => {
    if (mitra.target_area_override) return mitra.target_area_override.toLowerCase();
    
    const cityArr = Array.isArray(mitra.cities) ? mitra.cities : [];
    const cityStr = cityArr.join(' ').toLowerCase();

    // Deteksi Kota Spesifik
    if (cityStr.includes('bandung')) return 'bandung';
    if (cityStr.includes('semarang')) return 'semarang';
    if (cityStr.includes('yogyakarta') || cityStr.includes('jogjakarta')) return 'yogyakarta';
    if (cityStr.includes('surabaya')) return 'surabaya';
    if (cityStr.includes('denpasar')) return 'denpasar';
    if (cityStr.includes('makassar')) return 'makassar';
    if (cityStr.includes('manado')) return 'manado';
    if (cityStr.includes('balikpapan')) return 'balikpapan';
    if (cityStr.includes('batam')) return 'batam';
    if (cityStr.includes('palembang')) return 'palembang';
    if (cityStr.includes('pekanbaru')) return 'pekanbaru';
    if (cityStr.includes('medan')) return 'medan';
    
    // Default Grup Area Utama
    return 'jabodetabek';
  };

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTargets, setSavingTargets] = useState(null); // Ubah jadi null untuk simpan ID Level
  const [searchTerm, setSearchTerm] = useState('');
  

  useEffect(() => {
    fetchTargets();
    const unsubscribe = listenPartners();
    return () => { unsubscribe(); }; // Cukup bersihkan unsubscribe partners saja
  }, []);

  // Fungsi khusus untuk listen 5 riwayat terakhir per level
  

  // 2. Ambil Data Target Berjenjang dari Firestore
  const fetchTargets = async () => {
    try {
      const docRef = doc(db, 'app_settings', 'performance_targets');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setTargets(docSnap.data());
      }
    } catch (error) {
      console.error("Error fetching targets:", error);
    }
  };

  // 3. Simpan Target Berjenjang (Dipisah per level & rekam riwayat)
  const handleSaveTargets = async (lvlId, lvlName) => {
    setSavingTargets(lvlId); // Mengunci tombol yang sedang diklik saja
    try {
      const docRef = doc(db, 'app_settings', 'performance_targets');
      await setDoc(docRef, targets, { merge: true }); 

      // Hitung otomatis jumlah mitra yang berpotensi terdampak
      const affectedCount = partners.filter(p => {
        const pRegion = getPartnerRegion(p);
        const pRole = p.divisi === 'Therapist' ? 'therapist' : 'cleaner';
        return pRegion === activeRegion && pRole === activeRole;
      }).length;

      // Simpan Log/Riwayat
      await addDoc(collection(db, 'target_histories'), {
        levelId: lvlId,
        levelName: lvlName,
        region: activeRegion,
        role: activeRole,
        partnerCount: affectedCount,
        targetData: targets[activeKey]?.[lvlId] || {},
        timestamp: serverTimestamp()
      });

      alert(`Konfigurasi ${lvlName} area ${activeRegion.toUpperCase()} berhasil disimpan! Konfigurasi ini akan mulai dievaluasi pada tanggal 1 bulan berikutnya.`);
    } catch (error) {
      console.error("Error saving targets:", error);
      alert("Gagal menyimpan target.");
    } finally {
      setSavingTargets(null);
    }
  };

  // 4. Real-time Listener (Gabungan Cleaners & Therapists)
  const listenPartners = () => {
    setLoading(true);
    // Listener untuk Therapists
    const unsubT = onSnapshot(collection(db, 'therapists'), (snapT) => {
      const tData = snapT.docs.map(d => ({ id: d.id, ...d.data(), divisi: 'Therapist' }));
      
      // Listener untuk Cleaners
      onSnapshot(collection(db, 'cleaners'), (snapC) => {
        const cData = snapC.docs.map(d => ({ id: d.id, ...d.data(), divisi: 'Cleaner' }));
        setPartners([...tData, ...cData]);
        setLoading(false);
      });
    });
    return () => unsubT();
  };

  // Komponen Progress Bar Internal (Dengan Angka Target Handal)
  const PerformanceBar = ({ label, current, targetHandal, targetTop, color, unit = "" }) => {
    const valCurrent = Number(current) || 0;
    const valHandal = Number(targetHandal) || 0;
    const valTop = Number(targetTop) || 1; 
    
    const progressRatio = Math.min(Math.max((valCurrent / valTop) * 100, 0), 100);
    const handalRatio = Math.min(Math.max((valHandal / valTop) * 100, 0), 100);
    
    return (
      // Tambahkan mb-5 agar ada ruang ekstra ke bawah jika label Handal agak memakan tempat
      <div className="space-y-1 mb-5"> 
        <div className="flex justify-between text-[10px] font-bold uppercase">
          <span className="text-gray-400">{label}</span>
          <span className="text-white">{valCurrent}{unit} / {valTop}{unit}</span>
        </div>
        
        {/* Tambahkan mt-2 agar ada ruang di atas bar untuk label "Handal" */}
        <div className="relative w-full bg-gray-700 rounded-full h-1.5 mt-4">
          
          <div 
            className={`absolute top-0 left-0 h-1.5 rounded-full transition-all duration-500 ${color}`} 
            style={{ width: `${progressRatio}%` }}
          ></div>
          
          {/* Label Teks Handal (Angkanya) - Berada di atas marker */}
          <div 
            className="absolute text-[8px] font-bold text-orange-400 whitespace-nowrap"
            // Posisikan sedikit ke kiri agar teks berada di tengah-tengah marker
            style={{ left: `calc(${handalRatio}% - 12px)`, top: '-14px' }}
          >
            Handal ({valHandal})
          </div>

          <div 
            className="absolute top-[-3px] w-1 h-3 bg-orange-400 rounded-sm border border-gray-900"
            style={{ left: `calc(${handalRatio}% - 2px)` }}
          ></div>
        </div>
      </div>
    );
  };

    // 👈 TAMBAHAN: Fungsi untuk Export ke Excel
  const handleExportExcel = () => {
    // Siapkan data semua mitra yang sudah di-load
    const dataToExport = partners.map(p => {
      const currentLvl = p.level_mitra || 1;
      const partnerRegion = getPartnerRegion(p);
      const partnerRole = p.divisi === 'Therapist' ? 'therapist' : 'cleaner';
      const partnerKey = `${partnerRegion}_${partnerRole}`;

      const regionTargets = targets[partnerKey] || targets[`jabodetabek_${partnerRole}`] || {};
      const targetTop = regionTargets['lvl3'] || { targetOrderSelesai: 50, targetJamOnline: 150, targetOrderMasuk: 50 };

      const netSelesai = (p.perf_orderSelesai || 0) - (p.perf_orderBatal || 0);
      const jamOnline = ((p.perf_menitOnline || 0) / 60).toFixed(1);
      const netMasuk = (p.perf_orderMasuk || 0) - (p.perf_orderAbaikan || 0);

      let levelName = 'MITRA';
      if (currentLvl == 2) levelName = 'MITRA HANDAL';
      else if (currentLvl >= 3) levelName = 'TOP MITRA';

      return {
        "Nama Mitra": p.nama || p.name || 'Mitra Zimo',
        "Divisi": p.divisi || '-',
        "ID Number": p.id_number || '-',
        "Area": partnerRegion.toUpperCase(),
        "Level Mitra": levelName,
        "Order Selesai (Net)": netSelesai,
        "Jam Online": jamOnline,
        "Order Diterima (Net)": netMasuk,
        "Target Order Selesai (Top)": targetTop.targetOrderSelesai,
        "Target Jam Online (Top)": targetTop.targetJamOnline,
        "Target Order Diterima (Top)": targetTop.targetOrderMasuk
      };
    });

    // Susun struktur Excel agar ada judul dan tidak berantakan
    const wsData = [
      ["LAPORAN PERFORMA MITRA ZIMO"], // Baris Judul 1
      [`Tanggal Export: ${new Date().toLocaleString('id-ID')}`], // Baris Judul 2
      [""], // Baris Kosong (Spacer)
      ["Nama Mitra", "Divisi", "ID Number", "Area", "Level Mitra", "Order Selesai (Net)", "Jam Online", "Order Diterima (Net)", "Target Order Selesai (Top)", "Target Jam Online (Top)", "Target Order Diterima (Top)"] // Header Tabel
    ];

    // Gabungkan header dengan data mitra
    const finalData = [...wsData, ...dataToExport.map(item => Object.values(item))];
    
    // Buat Worksheet
    const ws = XLSX.utils.aoa_to_sheet(finalData);

    // Atur lebar kolom biar rapi dan jelas
    ws['!cols'] = [
      { wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 20 }, { wch: 12 }, { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 25 }
    ];

    // Merge cell untuk Judul utama dan tanggal export
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 10 } }, // Merge baris 1 kolom A sampai K
      { s: { r: 1, c: 0 }, e: { r: 1, c: 10 } }  // Merge baris 2 kolom A sampai K
    ];

    // Buat Workbook dan unduh file
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Performa Mitra");
    XLSX.writeFile(wb, `Laporan_Performa_Mitra_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const filteredPartners = searchTerm.trim() === '' 
    ? [] 
    : partners.filter(p => {
        const namaMitra = (p.nama || p.name || p.mitraName || '').toLowerCase();
        return namaMitra.includes(searchTerm.toLowerCase());
      });

  return (
    <div className="p-6 bg-gray-900 min-h-screen text-white space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Target Performa Berjenjang</h1>
          <p className="text-gray-400 text-sm">Sesuaikan tantangan untuk tiap level mitra</p>
        </div>
        <div className="bg-blue-900/20 border border-blue-800 p-2 px-4 rounded-lg flex items-center gap-3 text-blue-300 text-xs font-medium">
          <AlertCircle size={16} />
          <span>Reset & Check Level otomatis setiap tgl 1, 01:00 WIB</span>
        </div>
      </div>

      {/* SECTION 1: KONFIGURASI TARGET PER LEVEL */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Target className="text-blue-400" size={20} />
            <h2 className="font-bold">Pengaturan Target Bulanan</h2>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* 👈 TAMBAHAN: Dropdown Divisi */}
            <div className="flex items-center gap-2 bg-blue-900/30 border border-blue-800 rounded-lg px-3 py-1.5">
              <span className="text-xs text-blue-300">Divisi:</span>
              <select 
                value={activeRole}
                onChange={(e) => setActiveRole(e.target.value)}
                className="bg-transparent text-sm font-bold text-blue-400 outline-none cursor-pointer uppercase"
              >
                <option value="therapist">THERAPIST</option>
                <option value="cleaner">CLEANER</option>
              </select>
            </div>

            <div className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5">
              <span className="text-xs text-gray-400">Area:</span>
              <select 
                value={activeRegion}
                onChange={(e) => setActiveRegion(e.target.value)}
                className="bg-transparent text-sm font-bold text-white outline-none cursor-pointer uppercase"
              >
                <option value="jabodetabek">JABODETABEK</option>
                {/* ... daftar kota biarkan utuh ... */}
                <option value="bandung">BANDUNG</option>
                <option value="semarang">SEMARANG</option>
                <option value="yogyakarta">YOGYAKARTA</option>
                <option value="surabaya">SURABAYA</option>
                <option value="denpasar">DENPASAR</option>
                <option value="makassar">MAKASSAR</option>
                <option value="manado">MANADO</option>
                <option value="balikpapan">BALIKPAPAN</option>
                <option value="batam">BATAM</option>
                <option value="palembang">PALEMBANG</option>
                <option value="pekanbaru">PEKANBARU</option>
                <option value="medan">MEDAN</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { id: 'lvl2', name: 'TARGET: MITRA HANDAL', color: 'text-orange-400' },
            { id: 'lvl3', name: 'TARGET: TOP MITRA', color: 'text-yellow-400' }
          ].map((level) => {
            const lvl = level.id;
            return (
            <div key={lvl} className="p-4 bg-gray-900/50 rounded-lg border border-gray-700 space-y-4">
              <div className="flex justify-between items-center border-b border-gray-700 pb-2">
                <span className={`${level.color} font-bold text-sm uppercase`}>{level.name}</span>
                <TrendingUp size={14} className="text-gray-500" />
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] text-gray-500 block mb-1">TARGET ORDER SELESAI</label>
                  <input 
                    type="number" 
                    value={targets[activeKey]?.[lvl]?.targetOrderSelesai || 0}
                    onChange={(e) => setTargets(prev => ({ ...prev, [activeKey]: { ...(prev[activeKey] || {}), [lvl]: { ...((prev[activeKey] || {})[lvl] || {}), targetOrderSelesai: Number(e.target.value) || 0 } } }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-sm outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 block mb-1">TARGET JAM ONLINE</label>
                  <input 
                    type="number" 
                    value={targets[activeKey]?.[lvl]?.targetJamOnline || 0}
                    onChange={(e) => setTargets(prev => ({ ...prev, [activeKey]: { ...(prev[activeKey] || {}), [lvl]: { ...((prev[activeKey] || {})[lvl] || {}), targetJamOnline: Number(e.target.value) || 0 } } }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-sm outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 block mb-1">MIN. PENERIMAAN (ACCEPT)</label>
                  <input 
                    type="number" 
                    value={targets[activeKey]?.[lvl]?.targetOrderMasuk || 0}
                    onChange={(e) => setTargets(prev => ({ ...prev, [activeKey]: { ...(prev[activeKey] || {}), [lvl]: { ...((prev[activeKey] || {})[lvl] || {}), targetOrderMasuk: Number(e.target.value) || 0 } } }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-sm outline-none focus:border-blue-500"
                  />
                </div>
              </div>
              
              {/* Tombol Simpan per Level */}
              <div className="mt-5 flex justify-end border-t border-gray-700 pt-3">
                <button 
                  onClick={() => handleSaveTargets(lvl, level.name)}
                  disabled={savingTargets === lvl}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg text-xs font-bold transition disabled:opacity-50"
                >
                  {savingTargets === lvl ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                  Simpan {level.name}
                </button>
              </div>

            </div>
            );
          })}
        </div>
      </div>

      {/* HALAMAN KHUSUS: RINGKASAN TARGET SEMUA KOTA */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
        <h2 className="font-bold mb-4 flex items-center gap-2"><Target size={20} className="text-blue-400" /> Ringkasan Target Semua Kota (Berlaku Tgl 1 Bulan Berikutnya)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {[
            { id: 'lvl2', name: 'TARGET: MITRA HANDAL', color: 'text-orange-400' },
            { id: 'lvl3', name: 'TARGET: TOP MITRA', color: 'text-yellow-400' }
          ].map((level) => (
            <div key={level.id} className="space-y-3">
              <h3 className={`${level.color} font-bold text-sm uppercase border-b border-gray-700 pb-2`}>
                {level.name} - {activeRole}
              </h3>
              <div className="max-h-[500px] overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                {['jabodetabek', 'bandung', 'semarang', 'yogyakarta', 'surabaya', 'denpasar', 'makassar', 'manado', 'balikpapan', 'batam', 'palembang', 'pekanbaru', 'medan'].map((city) => {
                  const key = `${city}_${activeRole}`;
                  const tData = targets[key]?.[level.id] || { targetOrderSelesai: 0, targetJamOnline: 0, targetOrderMasuk: 0 };
                  return (
                    <div key={city} className="bg-gray-900 border border-gray-700 p-3 rounded-lg text-xs flex justify-between items-center hover:bg-gray-800 transition">
                      <span className="font-bold text-white uppercase w-28">{city}</span>
                      <div className="grid grid-cols-3 gap-2 text-center w-full max-w-[200px]">
                        <div><div className="text-gray-500 text-[9px]">ORDER</div><div className="font-bold text-white">{tData.targetOrderSelesai}</div></div>
                        <div><div className="text-gray-500 text-[9px]">JAM</div><div className="font-bold text-white">{tData.targetJamOnline}</div></div>
                        <div><div className="text-gray-500 text-[9px]">TERIMA</div><div className="font-bold text-white">{tData.targetOrderMasuk}</div></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

        </div>
      </div>

            {/* SECTION 2: MONITORING PERFORMA */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="relative max-w-md w-full">
            <Search className="absolute left-3 top-2.5 text-gray-500" size={18} />
            <input 
              type="text" 
              placeholder="Cari nama mitra..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm outline-none focus:border-blue-500 w-full"
            />
          </div>
          
          {/* 👈 TAMBAHAN: Tombol Export Excel */}
          <button 
            onClick={handleExportExcel}
            disabled={partners.length === 0}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-500 px-4 py-2 rounded-lg text-xs font-bold transition disabled:opacity-50 whitespace-nowrap"
          >
            <Download size={14} />
            Export ke Excel
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-900/50 text-gray-400 text-[10px] uppercase font-bold tracking-wider">
              <tr>
                <th className="px-6 py-4">Mitra</th>
                <th className="px-6 py-4">Progress KPI (Sesuai Level)</th>
                <th className="px-6 py-4 text-center">Info Level</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr><td colSpan="3" className="p-10 text-center text-gray-500">Memuat data...</td></tr>
              ) : searchTerm.trim() === '' ? (
                <tr><td colSpan="3" className="p-16 text-center text-gray-500 text-sm italic">Ketik nama mitra untuk memuat rapor individu</td></tr>
              ) : filteredPartners.map(p => {
                const currentLvl = p.level_mitra || 1;
                
                // ✨ PERBAIKAN: Identifikasi area DAN divisi mitra
                const partnerRegion = getPartnerRegion(p);
                const partnerRole = p.divisi === 'Therapist' ? 'therapist' : 'cleaner'; // 👈 Deteksi dari koleksi asalnya
                const partnerKey = `${partnerRegion}_${partnerRole}`;

                const regionTargets = targets[partnerKey] || targets[`jabodetabek_${partnerRole}`] || {};
                const targetHandal = regionTargets['lvl2'] || { targetOrderSelesai: 30, targetJamOnline: 100, targetOrderMasuk: 30 };
                const targetTop = regionTargets['lvl3'] || { targetOrderSelesai: 50, targetJamOnline: 150, targetOrderMasuk: 50 };

                const netSelesai = (p.perf_orderSelesai || 0) - (p.perf_orderBatal || 0);
                const jamOnline = ((p.perf_menitOnline || 0) / 60).toFixed(1);
                const netMasuk = (p.perf_orderMasuk || 0) - (p.perf_orderAbaikan || 0);

                // Logika Penamaan & Warna Badge Level
                let levelName = 'MITRA';
                let badgeClass = 'bg-gray-700 text-gray-300 border-gray-500';
                if (currentLvl == 2) {
                  levelName = 'MITRA HANDAL';
                  badgeClass = 'bg-orange-900/40 text-orange-400 border-orange-800';
                } else if (currentLvl >= 3) {
                  levelName = 'TOP MITRA';
                  badgeClass = 'bg-yellow-900/40 text-yellow-400 border-yellow-800';
                }

                return (
                  <tr key={p.id} className="hover:bg-gray-700/30 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-600 overflow-hidden border border-gray-700">
                          {p.photoUrl || p.foto ? <img src={p.photoUrl || p.foto} alt="" className="w-full h-full object-cover" /> : <Users className="p-2 w-full h-full text-gray-400" />}
                        </div>
                        <div>
                          <div className="font-bold text-sm text-white">{p.nama || p.name || 'Mitra Zimo'}</div>
                          <div className="text-[10px] text-blue-400 font-medium">{p.divisi} • {p.id_number}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 w-80">
                      <PerformanceBar label="Orderan" current={netSelesai} targetHandal={targetHandal.targetOrderSelesai} targetTop={targetTop.targetOrderSelesai} color="bg-green-500" />
                      <PerformanceBar label="Online" current={jamOnline} targetHandal={targetHandal.targetJamOnline} targetTop={targetTop.targetJamOnline} color="bg-blue-500" unit="j" />
                      <PerformanceBar label="Penerimaan" current={netMasuk} targetHandal={targetHandal.targetOrderMasuk} targetTop={targetTop.targetOrderMasuk} color="bg-orange-500" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className={`inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase border tracking-wider ${badgeClass} mb-1`}>
                        {levelName}
                      </div>
                      {/* ✨ TAMBAHAN: Indikator Area Mitra */}
                      <div className="text-[9px] text-gray-500 font-bold uppercase">
                        {p.target_area_override ? `🔒 ${partnerRegion}` : `📍 ${partnerRegion}`}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}